import Link from 'next/link'
import type { ReactNode } from 'react'
import { footerClickAttrs, type FooterClickMeta } from '../clickTracking'
import type { FooterLinkItem } from '../types'

interface FooterLinkProps {
  item: FooterLinkItem
  className?: string
  /** Clase de un `<span>` interno que envuelve el texto — separa el área de
   * toque (todo el enlace) del ancho del efecto visual de hover (subrayado
   * ceñido solo al texto). Si no se pasa, el texto va directo sin envolver. */
  textClassName?: string
  children?: ReactNode
  /** Metadatos de analítica (`click` del footer, ver `FooterClickTracker`). */
  tracking?: FooterClickMeta
}

/**
 * Enlace del footer: `next/link` para rutas internas, `<a>` con rel de
 * seguridad para externas. El nombre accesible sale de `ariaLabel` o `label`.
 */
export function FooterLink({ item, className, textClassName, children, tracking }: FooterLinkProps) {
  const label = children ?? item.label
  const content = textClassName ? <span className={textClassName}>{label}</span> : label
  const ariaLabel = item.ariaLabel ?? item.label
  const trackingAttrs = tracking
    ? footerClickAttrs({ ...tracking, linkText: tracking.linkText ?? item.label })
    : undefined

  if (item.external) {
    return (
      <a
        href={item.href}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel}
        {...trackingAttrs}
      >
        {content}
      </a>
    )
  }

  // Con ancla (`#…`), `<a>` nativo en vez de `next/link`: `Link` hace scroll al
  // `#id` pero no emite `hashchange`, y la página destino lo necesita para
  // abrir el elemento enlazado (p. ej. la pregunta de las FAQs, también
  // estando ya en esa página — ver `useLocationHash`).
  if (item.href.includes('#')) {
    return (
      <a href={item.href} className={className} aria-label={ariaLabel} {...trackingAttrs}>
        {content}
      </a>
    )
  }

  return (
    <Link href={item.href} className={className} aria-label={ariaLabel} {...trackingAttrs}>
      {content}
    </Link>
  )
}
